<script setup lang="ts">
import { Search as SearchIcon } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'
import { AgreementType } from '@/types/constantsEnum'
import GzhCodeImage from './components/GzhCodeImage.vue'
import { ref, watch, onMounted } from 'vue'
import userApi from '@/api/userApi'
import ContactDialog from './components/ContactDialog.vue'
import { useRouter } from 'vue-router'
import { Navigation } from '@/utils/navigation'
import { useSystemConfigStoreHook } from '@/store/modules/systemConfig'

const { t } = useI18n()
const router = useRouter()
const gzhCodeImageRef = ref<InstanceType<typeof GzhCodeImage> | null>(null)
const contactDialogRef = ref<InstanceType<typeof ContactDialog> | null>(null)
const systemConfigStore = useSystemConfigStoreHook()

// 使用单独的ref对象来存储FAQ和用户指南链接
const faqLink = ref()
const userGuideLink = ref()

export interface ContactInfo {
  wxQrCodeUrl: string
  email: string
  telephone: string
  wxGzQrCodeUrl: string
}

const contactInfo = ref<ContactInfo>({
  wxQrCodeUrl: '',
  email: '',
  telephone: '',
  wxGzQrCodeUrl: '',
})

// 新增处理特殊链接的函数
const getItemLink = (item: any) => {
  if (item.linkType === 'faq') {
    return faqLink.value
  } else if (item.linkType === 'userGuide') {
    return userGuideLink.value
  }
  return item.link
}

const pageRouter = (path: string, item?: any) => {
  const actualPath = item && item.linkType ? getItemLink(item) : path
  Navigation.openBlank(actualPath)
}

const showContactDialog = () => {
  contactDialogRef.value?.showDialog()
}

const showGzhDialog = () => {
  gzhCodeImageRef.value?.showDialog()
}

watch(
  () => systemConfigStore.systemConfig,
  (newConfig) => {
    if (newConfig) {
      // 更新独立的ref对象而不是数组中的值
      faqLink.value = newConfig.urlFaq
      userGuideLink.value = newConfig.urlUserGuide
    }
  },
  { immediate: true },
)

onMounted(async () => {
  userApi.getContactInfo().then((res) => {
    contactInfo.value = res.data
  })
})
</script>

<template>
  <footer class="w-full bg-[#000] py-[67px] flex flex-col">
    <div class="md:flex-row flex flex-col w-full max-w-maxWidth mx-auto">
      <div class="flex-1 flex flex-col items-center">
        <div class="flex items-center md:justify-start justify-center">
          <a href="/" class="flex items-center md:justify-start justify-center h-auto flex-1">
            <div class="flex flex-col items-center justify-center relative">
              <img src="@/assets/icon/logo.png" alt="logo" class="w-[10.25rem] object-cover" />
              <div
                class="flex items-center justify-center mt-[2.75rem] absolute bottom-[-2rem] left-[0] w-full"
              >
                <el-icon color="#fff" class="mr-[1.375rem]"><SearchIcon /></el-icon>
                <el-icon color="#fff" class="mr-[1.375rem]"><SearchIcon /></el-icon>
                <el-icon color="#fff"><SearchIcon /></el-icon>
              </div>
            </div>

            <div class="mx-[2rem] bg-[#D8D8D8] w-[1px] h-[90px]"></div>
            <div class="flex flex-col text-white text-size20 font-square-bold text-center">
              <span>{{ t('layoutFooter.title1') }}</span>
              <span>{{ t('layoutFooter.title2') }}</span>
              <span>{{ t('layoutFooter.title3') }}</span>
            </div>
          </a>
        </div>
      </div>
      <div class="flex-1 flex w-full md:justify-center justify-center md:mt-0 mt-[2.5rem]">
        <!-- 产品分类 -->
        <div class="flex flex-col mr-[9.5625rem]">
          <h3 class="text-white text-size20 font-square-bold">
            {{ t('layoutFooter.productTitle') }}
          </h3>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter('/music')"
            >
              {{ t('layoutFooter.productItem1') }}
            </span>
          </div>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter('/voice')"
            >
              {{ t('layoutFooter.productItem2') }}
            </span>
          </div>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter('/ai')"
            >
              {{ t('layoutFooter.productItem3') }}
            </span>
          </div>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter('/space')"
            >
              {{ t('layoutFooter.productItem5') }}
            </span>
          </div>
        </div>

        <!-- 关于我们分类 -->
        <div class="flex flex-col mr-[9.5625rem]">
          <h3 class="text-white text-size20 font-square-bold">
            {{ t('layoutFooter.aboutUsTitle') }}
          </h3>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter('/news')"
            >
              {{ t('layoutFooter.aboutUsItem1') }}
            </span>
          </div>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter('/html?type=' + AgreementType.CompanyProfile)"
            >
              {{ t('layoutFooter.aboutUsItem2') }}
            </span>
          </div>
        </div>

        <!-- 帮助中心分类 -->
        <div class="flex flex-col">
          <h3 class="text-white text-size20 font-square-bold">
            {{ t('layoutFooter.helpCenterTitle') }}
          </h3>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter(faqLink, { linkType: 'faq' })"
            >
              {{ t('layoutFooter.helpCenterItem1') }}
            </span>
          </div>
          <div class="flex flex-col mt-[1.4375rem]">
            <span
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor visited:text-white/50"
              @click="pageRouter(userGuideLink, { linkType: 'userGuide' })"
            >
              {{ t('layoutFooter.usageGuide') }}
            </span>
          </div>
          <div class="flex flex-col mt-[1.4375rem]">
            <div
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor"
              @click="showContactDialog(contactDialogRef)"
            >
              {{ t('layoutFooter.helpCenterItem2') }}
            </div>
          </div>
          <div class="flex flex-col mt-[1.4375rem]">
            <div
              class="text-white text-size14 font-PingFang-Regular hover:text-themeColor"
              @click="showGzhDialog(gzhCodeImageRef)"
            >
              {{ t('layoutFooter.helpCenterItem3') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col justify-center items-center mt-[30px]">
      <div class="flex items-center justify-center">
        <span class="text-white mr-[2px] text-size12">{{ t('layoutFooter.copyright') }}</span>
        <span
          class="text-themeColor2 hover:text-themeColor visited:text-themeColor2 mr-[2px] text-size12"
          @click="pageRouter('/html?type=' + AgreementType.Protocal)"
          >《{{ t('layoutFooter.userAgreement') }}》</span
        >
        <span
          class="text-themeColor2 hover:text-themeColor visited:text-themeColor2 mr-[2px] text-size12"
          @click="pageRouter('/html?type=' + AgreementType.Privacy)"
          >《{{ t('layoutFooter.privacyPolicy') }}》</span
        >
        <span
          class="text-themeColor2 hover:text-themeColor visited:text-themeColor2 text-size12"
          @click="pageRouter('/html?type=' + AgreementType.ServiceAgreement)"
          >《{{ t('layoutFooter.serviceTerms') }}》</span
        >
      </div>
      <div class="flex items-center justify-center mt-[1.875rem]">
        <span
          class="text-size12 text-white/50 hover:text-themeColor visited:text-white/50 mr-[12px]"
          @click="pageRouter('/html?type=' + AgreementType.CompanyProfile)"
          >深圳市晟诺科讯达科技有限责任公司</span
        >
        <span
          class="text-size12 text-white/50 hover:text-themeColor visited:text-white/50"
          @click="pageRouter('https://beian.miit.gov.cn/#/Integrated/recordQuery')"
        >
          {{ t('layoutFooter.icp') }}
        </span>
      </div>
    </div>
    <GzhCodeImage ref="gzhCodeImageRef" :wx-gz-qr-code-url="contactInfo.wxGzQrCodeUrl" />
    <ContactDialog ref="contactDialogRef" :contact-info="contactInfo" />
  </footer>
</template>

<style lang="scss" scoped></style>
